import { Alert, Table, TableColumn, TableRecord } from '@apex/component-library'
import { Box, Checkbox, Skeleton, Text } from '@chakra-ui/react'
import {
  ConfiguredTransferFeesType,
  FeeTransferType,
  TransferFeeDescriptionType,
  TransferFeeLabel,
  TransferFeesType
} from './types'
import React, { useEffect, useState } from 'react'

import { isEmpty } from 'lodash'
import { useFormatNumber } from '../../utils'
import { useFormikContext } from 'formik'
import useGetTransferFees from '../../api/useGetTransferFees'

interface TransferFeesProps {
  accountNumber: string
  feeTransferType: FeeTransferType
  applicableFees: TransferFeeDescriptionType[]
}

const TransferFees = ({ accountNumber, feeTransferType, applicableFees }: TransferFeesProps) => {
  const { error, data: feeData, isLoading } = useGetTransferFees(feeTransferType, accountNumber)
  const formik = useFormikContext()
  const formatNumber = useFormatNumber()

  const [configuredFees, setConfiguredFees] = useState<ConfiguredTransferFeesType>()
  const [initialFees, setInitialFees] = useState<ConfiguredTransferFeesType>()

  const getInitialFees = (): ConfiguredTransferFeesType => {
    let initialFees = {}

    if (!isLoading && feeData) {
      feeData.forEach((fee: any) => {
        if (applicableFees.includes(fee.type)) {
          let chargeCustomer = fee.correspondentNetAmount >= 0
          let customerDebit = fee.customerDebitAmount
          let correspondentNet = fee.correspondentNetAmount
          let apexCredit = fee.apexCreditAmount

          // preserve the user config if they have made changes
          if (configuredFees && !!configuredFees[fee.type]) {
            chargeCustomer = configuredFees[fee.type].chargeCustomer
            customerDebit = configuredFees[fee.type].customerDebit
            correspondentNet = configuredFees[fee.type].correspondentNet
            apexCredit = configuredFees[fee.type].apexCredit
          }

          initialFees = {
            ...initialFees,
            [fee.type]: {
              chargeCustomer,
              customerDebit,
              correspondentNet,
              apexCredit
            }
          }
        }
      })
    }
    return initialFees
  }

  const getFeesSummary = (fees: ConfiguredTransferFeesType): TransferFeesType => {
    let feesSummary = {}

    for (const fee in fees) {
      const chargeCustomer = fees[fee].correspondentNet >= 0
      const feeTotal = chargeCustomer ? fees[fee].customerDebit : Math.abs(fees[fee].correspondentNet)
      if (feeTotal > 0) {
        feesSummary = {
          ...feesSummary,
          [fee]: {
            chargeCustomer,
            feeTotal
          }
        }
      }
    }
    return feesSummary
  }

  useEffect(() => {
    setInitialFees(getInitialFees())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feeData, applicableFees])

  useEffect(() => {
    if (initialFees) {
      setConfiguredFees(initialFees)
      formik.setFieldValue('fees', getFeesSummary(initialFees))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialFees])

  useEffect(() => {
    applicableFees.forEach((fee) => {
      if (applicableFees.includes(fee)) {
        if (initialFees && !isEmpty(initialFees[fee])) {
          setConfiguredFees({
            ...configuredFees,
            [fee]: {
              chargeCustomer: initialFees[fee].chargeCustomer,
              customerDebit: initialFees[fee].customerDebit,
              correspondentNet: initialFees[fee].correspondentNet,
              apexCredit: initialFees[fee].apexCredit
            }
          })
        }
      } else {
        if (configuredFees) {
          delete configuredFees[fee]
        }
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicableFees])

  useEffect(() => {
    if (configuredFees) {
      formik.setFieldValue('fees', getFeesSummary(configuredFees))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configuredFees])

  const columns: TableColumn[] = [
    {
      key: 'chargeCustomer',
      label: 'Charge Customer',
      recordField: 'chargeCustomer',
      isSortable: false,
      cellRenderer: ({ value }: { value: JSX.Element }) => value
    },
    {
      key: 'feeDescription',
      label: 'Fee Description',
      recordField: 'feeDescription',
      isSortable: false
    },
    {
      key: 'customerDebit',
      label: 'Customer Debit',
      recordField: 'customerDebit',
      isSortable: false,
      alignment: 'right'
    },
    {
      key: 'correspondentNet',
      label: 'Correspondent Net',
      recordField: 'correspondentNet',
      isSortable: false,
      alignment: 'right'
    },
    {
      key: 'apexCredit',
      label: 'Apex Credit',
      recordField: 'apexCredit',
      isSortable: false,
      alignment: 'right'
    }
  ]

  const updateConfiguredFees = (feeType: string, chargeCustomer: boolean) => {
    if (!configuredFees || !initialFees) return
    if (chargeCustomer === initialFees[feeType].chargeCustomer) {
      setConfiguredFees({
        ...configuredFees,
        [feeType]: {
          chargeCustomer,
          customerDebit: initialFees[feeType].customerDebit,
          correspondentNet: initialFees[feeType].correspondentNet,
          apexCredit: initialFees[feeType].apexCredit
        }
      })
    } else {
      let customerDebit
      let correspondentNet

      if (chargeCustomer) {
        customerDebit = configuredFees[feeType].apexCredit
        correspondentNet = 0
      } else {
        customerDebit = 0
        correspondentNet = configuredFees[feeType].correspondentNet - configuredFees[feeType].customerDebit
      }

      setConfiguredFees({
        ...configuredFees,
        [feeType]: {
          chargeCustomer,
          customerDebit,
          correspondentNet,
          apexCredit: configuredFees[feeType].apexCredit
        }
      })
    }
  }

  const tableData = (): TableRecord[] => {
    const tableRowData: TableRecord[] = []

    for (const fee in configuredFees) {
      const checkboxElement = (
        <Checkbox
          name={'chargeTo'}
          aria-label={`${fee} checkbox`}
          onChange={(e) => {
            updateConfiguredFees(fee, e.target.checked)
          }}
          defaultChecked={configuredFees[fee].chargeCustomer}
        />
      ) as any

      tableRowData.unshift({
        chargeCustomer: checkboxElement,
        feeDescription: TransferFeeLabel[fee as TransferFeeDescriptionType],
        customerDebit: formatNumber(configuredFees[fee].customerDebit, {
          style: 'currency',
          currency: 'USD'
        }),
        correspondentNet: formatNumber(configuredFees[fee].correspondentNet, {
          style: 'currency',
          currency: 'USD'
        }),
        apexCredit: formatNumber(configuredFees[fee].apexCredit, {
          style: 'currency',
          currency: 'USD'
        }),
        key: fee
      })
    }

    return tableRowData
  }

  const transferFeeServerError = error?.response && error?.response.status !== 401
  const permissionError = error?.response && error?.response.status === 401
  const noFees = isEmpty(configuredFees) && !isLoading && !error
  return (
    <>
      <Text as='h3' textStyle='h3'>
        Fees
      </Text>
      <Text>Default assignment reflects fee schedule on record.</Text>
      <Table columns={columns} data={tableData()} size='md' data-testid='feesTable'></Table>
      {transferFeeServerError && (
        <Box minHeight='5' paddingBottom='5' borderBottom='1px'>
          <Alert
            status='warning'
            description='There was a problem retrieving fee information. Fees may be assessed during review.'
            showIcon={false}
          />
        </Box>
      )}
      {permissionError && (
        <Box minHeight='5' paddingBottom='5' borderBottom='1px'>
          <Alert
            status='warning'
            description='Additional permissions are required.'
            showIcon={false}
            data-testid='feesPermissionWarning'
          />
        </Box>
      )}
      {noFees && (
        <Box minHeight='5' paddingBottom='5' borderBottom='1px' fontStyle={'italic'} data-testid='noFees'>
          No Fees. (Fees may be assessed during review.)
        </Box>
      )}
      {isLoading && !error && (
        <Box>
          <Skeleton h='20px' />
          <Skeleton marginTop='5' h='20px' />
        </Box>
      )}
    </>
  )
}

export default TransferFees
